import classNames from "classnames";
import type { DoubleEntryTableColumns, DoubleEntryTableRows } from "components/DoubleEntryTable";
import DoubleEntryTable from "components/DoubleEntryTable";
import ControlFilter from "components/shared/ControlFilter";
import DateRangeSelectors from "components/shared/DateRangeSelectors";
import { useDateSelectorShortcuts } from "components/shared/DateRangeSelectors/utils";
import type { FilterOption } from "components/shared/FiltersAutocomplete";
import FiltersAutocomplete from "components/shared/FiltersAutocomplete";
import { SmallBody, SmallBodyHighlight, TinyBody } from "helpers/TypographyComponents";
import { groupObjByKey, isObjectEmpty } from "helpers/utils";
import useMounted from "hooks/useMounted";
import type { ChangeEvent, SetStateAction } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AnalyticsChartType } from "store/analyticsStore";
import { fetchAnalyticsChannelsPeriodSales, getStoreKeyFromFilters } from "store/analyticsStore";
import type { TPeriod } from "store/periodStore";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import type { AnalyticsFilters, IDatesRange } from "types";

import LoadingBlurFilter from "../LoadingBlurFilter";
import type { DataType } from "../SalesComparisonChart/utils";
import { resolveAnalyticsDataType, useAnalyticsDataTypeOptions } from "../useAnalyticsDataTypeOptions";
import type { StoredPreferences } from "../utils";
import { ANALYTICS_DATE_PICKER_MAX_END, hydrateDates, settingsTransform } from "../utils";
import styles from "./style.module.scss";
import type { ChannelsMetricsByKind, ChosenOptions } from "./utils";
import { channelsRowsTransform, parseChannelPeriodSales } from "./utils";

/**
 * Render Channels Metrics indicators table
 * Show sales per channels / channels kind on year N or N-1
 * Based on dataType turnover or quantities
 */
interface IChannelsMetrics {
    /**
     * Channels sales data
     */
    // channelsSales: ChannelSales[];
    currentPeriod: TPeriod;
    /**
     * Stored preferences for filters
     */
    storedPreferences: StoredPreferences;
    /**
     * Options for titles filter
     */
    titleOptions: FilterOption[];
    /**
     * Data type
     */
    type: AnalyticsChartType;
    /**
     * Data for titles pagination from useAPIPagination hook
     */
    titlesPaginationData: {
        handleQueryTitle: (e: React.ChangeEvent<HTMLInputElement>) => void;
        setOffsetPaginationTitles: React.Dispatch<SetStateAction<number>>;
        requestStatusTitle: TFetchStatus;
    };
    /**
     * Dates range for periods
     */
    periodsDateRange: IDatesRange;
}

const ChannelsMetrics = ({
    currentPeriod,
    storedPreferences,
    titleOptions,
    type,
    titlesPaginationData,
    periodsDateRange,
}: IChannelsMetrics) => {
    const mounted = useMounted(1000);
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const {
        chosenDates: storedDates,
        chosenTitles: storedTitles,
        dataType: storedDataType,
    } = storedPreferences?.storedFilters?.[type] || {};

    const { handleQueryTitle, setOffsetPaginationTitles, requestStatusTitle } = titlesPaginationData;

    const { analyticsChannelsPeriodSalesStatus, analyticsChannelsPeriodSales, refreshAnalyticsStatus } = useAppSelector(
        (state) => state.analytics,
    );
    const { periodsShortcuts } = useDateSelectorShortcuts();
    const { dataTypeOptions, subtitleByDataType, labelsReady } = useAnalyticsDataTypeOptions();

    const isLoading = analyticsChannelsPeriodSalesStatus === "pending" || refreshAnalyticsStatus === "pending";

    const [chosenOptions, setChosenOptions] = useState<ChosenOptions>({
        chosenDates: hydrateDates(storedDates) || periodsDateRange,
        chosenTitles: storedTitles || [],
        dataType: (storedDataType as DataType) || "turnover",
    });

    const { chosenDates, chosenTitles } = chosenOptions;
    const dataType = resolveAnalyticsDataType(chosenOptions.dataType, dataTypeOptions, labelsReady);

    useEffect(() => {
        if (periodsDateRange?.startDate && isObjectEmpty(chosenDates)) {
            handleChooseOption("chosenDates", periodsDateRange);
        }
    }, [currentPeriod]);

    /**
     * Save all options/filters in company settings
     */
    useEffect(() => {
        if (!mounted) return;
        const settingsValue = settingsTransform("filters", { ...chosenOptions, dataType }, storedPreferences, type);
        dispatch(putCompanySettings([{ settingsName: "analytics_layout", settingsValue }]));
    }, [chosenOptions]);

    const columns: DoubleEntryTableColumns[] = [
        { label: "N", align: "right" },
        { label: "N-1", align: "right" },
        { label: "Index", align: "right" },
    ];

    const handleChangeTitlesInput = (e: ChangeEvent<HTMLInputElement>) => {
        handleQueryTitle(e);
    };

    const resetTitleQuery = () => {
        handleQueryTitle({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
    };

    const handleScrollTitlesEnd = () => setOffsetPaginationTitles(titleOptions?.length ?? 0);

    const handleChooseOption = (optionKey: string, value: any) => {
        setChosenOptions((prev) => ({ ...prev, [optionKey]: value }));
    };

    const unselectOption = (optionKey: string, optionId: number) => {
        setChosenOptions((prev) => {
            const updatedOption = prev[optionKey].filter((item) => item.id !== optionId);
            return { ...prev, [optionKey]: updatedOption };
        });
    };

    const filters = useMemo<AnalyticsFilters | null>(() => {
        return chosenDates
            ? {
                  dates: chosenDates,
                  titleIds: chosenTitles.map((o) => `${o.id}`),
              }
            : null;
    }, [chosenDates, chosenTitles]);

    const parsedChannelSales = useMemo(() => {
        if (!filters) {
            return [];
        }
        const storeKey = getStoreKeyFromFilters(filters);
        const channelPeriodSales = analyticsChannelsPeriodSales[storeKey];

        return channelPeriodSales ? parseChannelPeriodSales(channelPeriodSales) : [];
    }, [filters, analyticsChannelsPeriodSales]);

    const rows: DoubleEntryTableRows[] = useMemo(() => {
        const channelsMetricsByKind = groupObjByKey("kind", parsedChannelSales) as ChannelsMetricsByKind;
        return channelsRowsTransform(channelsMetricsByKind, dataType);
    }, [parsedChannelSales, dataType]);

    useEffect(() => {
        if (filters) {
            dispatch(fetchAnalyticsChannelsPeriodSales(filters));
        }
    }, [dispatch, filters]);

    return (
        <>
            {/** GRAPH LOADING FILTER */}
            {isLoading && <LoadingBlurFilter />}

            {/** HEADER */}
            <div className={classNames({ [styles["channels-metrics-header"]]: true, [styles["loading"]]: isLoading })}>
                <div className={styles["channels-metrics-label"]}>
                    <SmallBodyHighlight>{t("Analytics.ChannelsIndicators")}</SmallBodyHighlight>
                    <SmallBody color="grey">vs N-1</SmallBody>
                </div>
                <div className={styles["date-range-filters"]}>
                    {!isObjectEmpty(chosenDates) && (
                        <DateRangeSelectors
                            dateRange={periodsDateRange}
                            maxEndDate={ANALYTICS_DATE_PICKER_MAX_END}
                            label={t("Analytics.DataBetween")}
                            defaultValues={chosenDates}
                            rangeShortcuts={periodsShortcuts}
                            handleChanges={(newDatesRange) => handleChooseOption("chosenDates", newDatesRange)}
                        />
                    )}
                </div>
            </div>

            {/** FILTERS */}
            <div className={styles["channels-metrics-filters"]}>
                <FiltersAutocomplete
                    label={<></>}
                    placeholder={t("Analytics.AllTitles")}
                    filtersOptions={titleOptions}
                    chosenOptions={chosenTitles}
                    handleChooseOption={(value) => handleChooseOption("chosenTitles", value)}
                    unselectOption={(optionId) => unselectOption("chosenTitles", optionId)}
                    onInputChange={handleChangeTitlesInput}
                    onScrollEnd={handleScrollTitlesEnd}
                    onScrollEndLoading={requestStatusTitle}
                    onClose={() => resetTitleQuery()}
                    optionAllName={t("Analytics.AllTitles")}
                    showOptionAll
                    limitTags={3}
                    disableCloseOnSelect
                    size="small"
                />
            </div>

            {/** TABLE */}
            <DoubleEntryTable
                tableLabel={t("Analytics.Categories")}
                columns={columns}
                rows={rows}
                collapsedTableLabel={t("Analytics.Channels")}
                isLoading={isLoading}
            />

            {/** GRAPH FOOTER */}
            <div className={styles["channels-metrics-footer"]}>
                <TinyBody color="grey">
                    {"Base :"} {subtitleByDataType[dataType]}
                </TinyBody>
                <div className={styles["channels-metrics-footer-right"]}>
                    <TinyBody color="grey" style={{ marginRight: "0.25rem" }}>
                        {t("Analytics.Dimension")}
                    </TinyBody>
                    <ControlFilter
                        controlValue={dataType}
                        filterOptions={dataTypeOptions}
                        onValueChange={(value) => handleChooseOption("dataType", value)}
                    />
                </div>
            </div>
        </>
    );
};

export default React.memo(ChannelsMetrics);
